import axios from "axios";

function isLocalDevHost(host) {
  return (
    host === "localhost" ||
    host === "127.0.0.1" ||
    /^192\.168\.\d{1,3}\.\d{1,3}$/.test(host) ||
    /^10\.\d{1,3}\.\d{1,3}\.\d{1,3}$/.test(host) ||
    /^172\.(1[6-9]|2\d|3[0-1])\.\d{1,3}\.\d{1,3}$/.test(host)
  );
}

const HOST = typeof window !== "undefined" ? window.location.hostname.toLowerCase() : "";
const CONFIGURED_BACKEND = (process.env.REACT_APP_BACKEND_URL || "https://rpsignage.com").replace(/\/$/, "");
const LOCAL_BACKEND = CONFIGURED_BACKEND
  .replace("http://127.0.0.1:", "http://localhost:")
  .replace("https://127.0.0.1:", "https://localhost:");
const USE_DEV_PROXY =
  process.env.NODE_ENV === "development" &&
  isLocalDevHost(HOST) &&
  !["localhost", "127.0.0.1"].includes(HOST);
const LAN_BACKEND = (() => {
  if (!USE_DEV_PROXY) return "";
  try {
    const url = new URL(LOCAL_BACKEND);
    url.hostname = HOST;
    return url.origin;
  } catch {
    return LOCAL_BACKEND;
  }
})();
const RAW_BASE = USE_DEV_PROXY
  // LAN devices must call the backend directly. The CRA proxy can serve GET
  // requests here, but its consumed JSON stream leaves POST bodies pending.
  ? LAN_BACKEND
  : (HOST === "localhost" ? LOCAL_BACKEND : CONFIGURED_BACKEND);
const BASE = RAW_BASE.replace(/\/api$/, "");
export const API_BASE = `${BASE}/api`;
// The real public web origin (e.g. https://rpsignage.com). Inside the
// Capacitor Android app, window.location.origin is "https://localhost"
// (the WebView's internal origin for bundled assets) - NOT a reachable
// address for anyone else. Any link meant to be shared outside the app
// (visitor approval links, etc.) must use this instead of
// window.location.origin.
export const PUBLIC_BASE_URL = BASE;

/** Turn stored media paths into absolute backend URLs for <img src>. */
export function resolveMediaUrl(url) {
  if (!url || typeof url !== "string") return "";
  const trimmed = url.trim();
  if (!trimmed) return "";
  if (/^data:/i.test(trimmed)) return trimmed;

  const serveMatch = trimmed.match(/\/api\/media\/serve\/([^/?#]+)/i);
  if (serveMatch) {
    return `${API_BASE}/media/serve/${serveMatch[1]}`;
  }

  if (/^https?:\/\//i.test(trimmed)) return trimmed;

  if (trimmed.startsWith("/")) return `${BASE}${trimmed}`;
  return `${BASE}/${trimmed}`;
}

export const api = axios.create({
  baseURL: API_BASE,
  withCredentials: true,
  timeout: 15000,
  headers: { "Content-Type": "application/json" },
});

export function apiDebugTarget() {
  return API_BASE;
}

// Ravi: "this can lead to issue like guard or owner cannot contact when
// required if logged out" -- the 12h access token had no way to renew
// itself, so a guard mid-shift (or anyone leaving the app open overnight)
// just hit a raw "Token expired" error with dead buttons and no way back
// in short of manually finding the login screen. Login already issues a
// long-lived refresh_token (7-30 days); this silently swaps in a fresh
// access token on the first 401 and retries the request once. Only if the
// refresh token itself is gone/expired do we clear storage and send the
// user back to /login, with a clear "session expired" message instead of a
// stuck error banner.
// A stable per-install id (NOT the refresh token itself) that lets the
// backend recognize "this is the same phone/browser as last time" across
// logins and refreshes, which is what makes per-device session listing and
// revocation possible. Generated once and kept in localStorage indefinitely
// (not cleared on logout) so revoking/re-registering a specific device
// stays consistent even across multiple login/logout cycles on it.
export function getOrCreateDeviceId() {
  try {
    let id = localStorage.getItem("device_id");
    if (!id) {
      id = (typeof crypto !== "undefined" && crypto.randomUUID) ? crypto.randomUUID() : `dev-${Date.now()}-${Math.random().toString(16).slice(2)}`;
      localStorage.setItem("device_id", id);
    }
    return id;
  } catch {
    return "";
  }
}

let refreshInFlight = null;

async function performTokenRefresh() {
  const rt = localStorage.getItem("refresh_token") || sessionStorage.getItem("refresh_token") || null;
  const { data } = await axios.post(`${API_BASE}/auth/refresh`, { refresh_token: rt, deviceId: getOrCreateDeviceId() }, { withCredentials: true });
  if (data?.access_token) {
    const store = sessionStorage.getItem("access_token") ? sessionStorage : localStorage;
    store.setItem("access_token", data.access_token);
  }
  if (data?.refresh_token) {
    const store = localStorage.getItem("refresh_token") ? localStorage : sessionStorage;
    store.setItem("refresh_token", data.refresh_token);
  }
  return data?.access_token || "";
}

// Decodes the "exp" claim from a JWT without verifying it - just a client
// side hint for whether it's worth refreshing proactively, never trusted
// for anything security-relevant (the backend always re-validates).
function tokenExpiresWithinMs(token, windowMs) {
  try {
    const payload = JSON.parse(atob(token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/")));
    if (!payload?.exp) return false;
    return payload.exp * 1000 - Date.now() < windowMs;
  } catch {
    return true;
  }
}

// A guard's phone typically sits untouched for hours between visitors, so
// "open the app and immediately dial a call" is very often the very first
// request made after a long background period - the worst possible moment
// to discover the access token expired reactively, since that puts the
// refresh round-trip directly in the way of an urgent call. Refreshing
// proactively as soon as the app comes back to the foreground means the
// token is already fresh by the time anyone taps anything. visibilitychange
// fires reliably inside the Capacitor WebView on resume, same as it does in
// a normal browser tab.
function refreshIfCloseToExpiry() {
  const access = sessionStorage.getItem("access_token") || localStorage.getItem("access_token");
  const refresh = localStorage.getItem("refresh_token") || sessionStorage.getItem("refresh_token");
  if (!refresh || !access) return;
  if (!tokenExpiresWithinMs(access, 30 * 60 * 1000)) return;
  if (!refreshInFlight) {
    refreshInFlight = performTokenRefresh().catch(() => {}).finally(() => { refreshInFlight = null; });
  }
}

if (typeof document !== "undefined") {
  // visibilitychange only fires on a state *change*, so it covers resuming
  // from background but not a cold app launch (the WebView's very first
  // load is already "visible", nothing transitions into that state). Check
  // once immediately too, so a fresh app open - not just a resume - also
  // gets ahead of an already-close-to-expiring token before the person can
  // tap anything.
  refreshIfCloseToExpiry();
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") refreshIfCloseToExpiry();
  });
}

function forceReLogin() {
  sessionStorage.removeItem("access_token");
  localStorage.removeItem("access_token");
  localStorage.removeItem("refresh_token");
  sessionStorage.removeItem("refresh_token");
  try { sessionStorage.setItem("auth_session_expired", "1"); } catch {}
  // A hard window.location.href redirect here used to send EVERY failed
  // request straight to /login unconditionally - including the routine
  // background "am I logged in" check that runs on every page load, public
  // pages included. Any browser/phone with a stale expired token (which,
  // after a day of testing, is most of them) got yanked to /login even on
  // the public marketing homepage, which was never supposed to require
  // login at all. Dispatching an event instead lets AuthContext update its
  // React state, so the existing role/shell-aware routing (already correct
  // before this) decides the right destination itself: a public page just
  // shows its normal logged-out content, while an actual protected screen
  // (or the native guard/resident app) still redirects to login exactly as
  // before - no more blunt, one-size-fits-all forced navigation.
  if (typeof window !== "undefined") window.dispatchEvent(new Event("rp:auth-invalidated"));
}

api.interceptors.response.use(
  (res) => res,
  async (error) => {
    const status = error?.response?.status;
    const cfg = error?.config || {};
    const url = cfg.url || "";
    const isAuthRoute = url.includes("/auth/login") || url.includes("/auth/refresh") || url.includes("/auth/logout");
    const hasRefreshToken = !!(localStorage.getItem("refresh_token") || sessionStorage.getItem("refresh_token"));
    if (status !== 401 || isAuthRoute || cfg._retried) {
      return Promise.reject(error);
    }
    // A session with an access token but no refresh token (logged in before
    // this device-session system existed, and never logged out/in since)
    // used to just reject here forever - the app kept LOOKING logged in
    // (nothing cleared that state) while every real action silently died
    // with a raw "Token expired" error, over and over, with no way out
    // short of the person figuring out on their own that they needed to
    // manually log out and back in. That's exactly what looked like "the
    // token keeps expiring and nothing fixes it" from a guard/resident who
    // has no reason to know that. Send them to a real login screen instead,
    // once, with a clear reason - one clean re-login and it's fixed for
    // good, instead of a silent dead end on every call attempt after.
    if (!hasRefreshToken) {
      forceReLogin();
      return Promise.reject(error);
    }
    cfg._retried = true;
    try {
      if (!refreshInFlight) {
        refreshInFlight = performTokenRefresh().finally(() => { refreshInFlight = null; });
      }
      const newAccess = await refreshInFlight;
      if (!newAccess) throw new Error("refresh returned no access token");
      cfg.headers = cfg.headers || {};
      cfg.headers["Authorization"] = `Bearer ${newAccess}`;
      return api(cfg);
    } catch {
      forceReLogin();
      return Promise.reject(error);
    }
  }
);

// attach token from localStorage as Bearer fallback (works if cookies blocked)
api.interceptors.request.use((cfg) => {
  const t = sessionStorage.getItem("access_token") || localStorage.getItem("access_token");
  const hasAuth = cfg.headers?.Authorization || cfg.headers?.authorization;
  if (t && !hasAuth) cfg.headers["Authorization"] = `Bearer ${t}`;
  if (typeof FormData !== "undefined" && cfg.data instanceof FormData) {
    delete cfg.headers["Content-Type"];
    delete cfg.headers["content-type"];
  }
  return cfg;
});

export function formatErr(detail) {
  if (detail == null) return "Something went wrong.";
  if (detail?.response?.data?.detail) return formatErr(detail.response.data.detail);
  if (detail?.response?.data?.message) return formatErr(detail.response.data.message);
  if (detail?.response?.status) return `Backend returned ${detail.response.status}.`;
  if (detail && detail.code === "ECONNABORTED") return "Request timed out. Backend service is not responding.";
  if (detail && detail.message && String(detail.message).toLowerCase().includes("timeout")) return "Request timed out. Backend service is not responding.";
  if (detail && detail.message && detail.request && !detail.response) return `Cannot reach backend service at ${API_BASE}.`;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}
